import {ReactNode} from 'react';
import Banner, {BannerTypes} from 'patient-ping-remedy/packages/banner';
import {StandardSemiBoldTypography} from 'patient-ping-remedy/packages/typography';
import Loading from '@common/Loading';
import {NoElementsWrapper, MarkAllAsRead} from '@common/widgets/WidgetElementList.styles';
import {WidgetGroup, WidgetCategoryHeaderWrapper, WidgetCategoryHeader, ViewAllWrapper, ViewAll}
  from '@common/widgets/popover/WidgetPopoverContent.styles';
import DisplayHelpers from '@/helpers/display_helpers';
import Helpers from '@/helpers/helpers';

type Props<T> = {
  elements: T[];
  totalElements: number;
  totalUnreadElements: number;
  dateProperty: string;
  title: string;
  count: number;
  isLoading: boolean;
  isApiLoading: boolean;
  error: Error | null;
  markAllAsRead(): void;
  navigate(): void;
  render(element: T): ReactNode;
}

export default function WidgetPopoverContent<T>({
  elements,
  totalElements,
  totalUnreadElements,
  dateProperty,
  title,
  count,
  isLoading,
  isApiLoading,
  error,
  markAllAsRead,
  navigate,
  render,
}: Props<T>) {
  const visibleElements = elements.slice(0, count);
  const excessiveElementsCount = totalElements - visibleElements.length;
  const categorizedElements = Helpers.getCategorizedElements(visibleElements, dateProperty);
  const todayElements = categorizedElements.todayElements;
  const yesterdayElements = categorizedElements.yesterdayElements;
  const olderElements = categorizedElements.olderElements;

  if (isLoading) {
    return <Loading />;
  }

  if (elements.length === 0) {
    return (
      <NoElementsWrapper center>
        <StandardSemiBoldTypography>All caught up! No unread {title.toLowerCase()}.</StandardSemiBoldTypography>
      </NoElementsWrapper>
    );
  }

  const markAllAsReadElement = (
    <MarkAllAsRead
      disabled={isApiLoading || totalUnreadElements === 0}
      onClick={() => markAllAsRead()}
      data-testid="mark-all-as-read"
    >
      Mark all as read
    </MarkAllAsRead>
  );

  return (
    <>
      {error && (
        <Banner type={BannerTypes.WARNING}>{DisplayHelpers.displayErrorMessage(error)}</Banner>
      )}

      {todayElements.length > 0 && (
        <WidgetGroup>
          <WidgetCategoryHeaderWrapper>
            <WidgetCategoryHeader>Today</WidgetCategoryHeader>
            {markAllAsReadElement}
          </WidgetCategoryHeaderWrapper>
          {todayElements.map(render)}
        </WidgetGroup>
      )}

      {yesterdayElements.length > 0 && (
        <WidgetGroup>
          <WidgetCategoryHeaderWrapper>
            <WidgetCategoryHeader>Yesterday</WidgetCategoryHeader>
            {todayElements.length === 0 && markAllAsReadElement}
          </WidgetCategoryHeaderWrapper>
          {yesterdayElements.map(render)}
        </WidgetGroup>
      )}

      {olderElements.length > 0 && (
        <WidgetGroup>
          <WidgetCategoryHeaderWrapper>
            <WidgetCategoryHeader>Older</WidgetCategoryHeader>
            {todayElements.length === 0 && yesterdayElements.length === 0 && markAllAsReadElement}
          </WidgetCategoryHeaderWrapper>
          {olderElements.map(render)}
        </WidgetGroup>
      )}

      {excessiveElementsCount > 0 && (
        <ViewAllWrapper>
          <ViewAll onClick={navigate}>
            View all (+{excessiveElementsCount})
          </ViewAll>
        </ViewAllWrapper>
      )}
    </>
  );
}
